import {Yup} from 'utils/validators';
import {isNil} from 'lodash-es';

/**
 * @constant RATE_THRESHOLD_POLICIES - policies for rate thresholds
 * @type {{weekdayFloorRate: string, ceilingRate24Hour: string, commuterCeilingRate: string, overnightFloorRate: string, weekendFloorRate: string, floorRate24Hour: string, commuterFloorRate: string}}
 */
export const RATE_THRESHOLD_POLICIES = {
    floorRate24Hour: '24_hour_floor_rate',
    ceilingRate24Hour: '24_hour_ceiling_rate',
    commuterFloorRate: 'commuter_floor_rate',
    commuterCeilingRate: 'commuter_ceiling_rate',
    overnightFloorRate: 'overnight_floor_rate',
    weekdayFloorRate: 'weekday_floor_rate',
    weekendFloorRate: 'weekend_floor_rate',
};

/**
 * @constant {object} ERROR_MESSAGE_RATE_THRESHOLDS - Error messages for rate threshold validation
 * @type {{floorMoreThan: string, floorLessThan: string, ceilingMoreThan: string, policyMoreThan: string}}
 */
export const ERROR_MESSAGE_RATE_THRESHOLDS = {
    number: 'This field must be a number.',
    floorMoreThan:
        'Floor rate must be >= 0. If you do not want to set a floor rate, please leave it blank and save.',
    floorLessThan: 'Floor rate must be less than $250.',
    ceilingMoreThan:
        'Ceiling rate must be more than $2. If you do not want to set a ceiling rate, please leave it blank and save.',
    ceilingLessThan: 'Ceiling rate must be less than $10,000.',
    policyMoreThan:
        'The ceiling value must be greater than the floor value for this rate policy. If you do not want to set a ceiling/floor rate, please leave it blank and save.',
};

/**
 * A Yup schema that validates a collection of rate thresholds
 * @async
 * @return {(Yup.ValidationError|TRateThreshold[])}
 */
export const rateThresholdSchema = Yup.array().of(
    Yup.object({
        threshold: Yup.number()
            .transform((value, originalValue, context) => {
                // if the value is already cast to the correct type, return it
                if (context.isType(value)) {
                    return value;
                }

                // otherwise parse the value & remove any commas
                return Number(originalValue.replace(/,/g, ''));
            })
            .typeError(ERROR_MESSAGE_RATE_THRESHOLDS.number)
            .nullable()
            .moreThan(-1, ERROR_MESSAGE_RATE_THRESHOLDS.floorMoreThan)
            .when(['policy'], {
                is: (policy) => policy.type === 'floor',
                then: (schema) =>
                    schema.lessThan(
                        250,
                        ERROR_MESSAGE_RATE_THRESHOLDS.floorLessThan
                    ),
                otherwise: (schema) =>
                    schema.when(['policy'], {
                        is: (policy) => policy.type === 'ceiling',
                        then: schema
                            .moreThan(
                                2,
                                ERROR_MESSAGE_RATE_THRESHOLDS.ceilingMoreThan
                            )
                            .lessThan(
                                10000,
                                ERROR_MESSAGE_RATE_THRESHOLDS.ceilingLessThan
                            ),
                    }),
            }),
    }).comparePropertiesInCollection(
        ERROR_MESSAGE_RATE_THRESHOLDS.policyMoreThan,
        (value, item) => {
            const isCommuterThreshold =
                value.policy.policy ===
                    RATE_THRESHOLD_POLICIES.commuterFloorRate &&
                item.policy.policy ===
                    RATE_THRESHOLD_POLICIES.commuterCeilingRate;

            const is24HourThreshold =
                value.policy.policy ===
                    RATE_THRESHOLD_POLICIES.floorRate24Hour &&
                item.policy.policy ===
                    RATE_THRESHOLD_POLICIES.ceilingRate24Hour;

            const thresholdIsNull =
                isNil(value.threshold) || isNil(item.threshold);

            return (
                item.id !== value.id &&
                !thresholdIsNull &&
                (isCommuterThreshold || is24HourThreshold) &&
                value.threshold > item.threshold
            );
        }
    )
);
